//
// Centralised wrapper for **/api/SystemNotifications** endpoints.
// All requests automatically attach the current JWT.
//
// Typical usage:
//
//   import SystemNotificationService from '../api/system-notification.service';
//
//   // ── administrator ──────────────────────────────────────
//   await SystemNotificationService.admin.create({
//     title:        'Payroll deadline ‼️',
//     body:         'Upload salary slips before 20-Jun.',
//     imageFile:    optionalFile,      // React-Native `File` / `Blob`
//     audienceType: 31,                // Enums.AudienceType.All  (example)
//     customUserIds:[ ],               // only if audienceType === Custom
//     startAtUtc:   new Date().toISOString(),
//     expiresAtUtc: new Date(Date.now() + 7*864e5).toISOString(),
//     sendPush:     true               // fire immediate FCM / SignalR
//   });
//
//   // ── any logged-in user ────────────────────────────────
//   const feed = await SystemNotificationService.user.feed({
//     userId:   'C123',
//     userType: 1                // Enums.UserType.Client
//   });
//
// All methods throw on non-2xx HTTP status so you can `try / catch`.
//

import config from '../config';
import selectedEnvironment from '../env';

const BASEAPI = config[selectedEnvironment].API_URL;
const ROOT = `${BASEAPI}/SystemNotifications`;

/*──────────────────────── helpers ────────────────────────*/
const getCurrentToken = async () => {
  const token = localStorage.getItem('token');
  return token != null ? JSON.parse(token) : null;
};

const jsonHeaders = async () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${await getCurrentToken()}`
});

const authHeaderOnly = async () => ({
    Authorization: `Bearer ${await getCurrentToken()}`
});

const guard = async (res) => {
    if (!res.ok) {
        const txt = await res.text();
        let msg;
        try { msg = JSON.parse(txt).error ?? txt; }
        catch { msg = txt; }
        throw new Error(msg || `HTTP ${res.status}`);
    }
    return res;
};

/*──────────────────────── administrator API ─────────────*/
const admin = {
    /* create – multipart/form-data  ------------------------*/
    create: async ({
        title,
        body,
        imageFile,        // File | Blob | undefined
        audienceType,
        customUserIds = [],
        startAtUtc,
        expiresAtUtc,
        sendPush = false
    }) => {
        const fd = new FormData();
        fd.append('Title', title);
        fd.append('Body', body);
        fd.append('AudienceType', audienceType);
        /* custom ids as JSON string for backend binder */
        if (customUserIds.length) fd.append('CustomUserIds', JSON.stringify(customUserIds));
        fd.append('StartAtUtc', startAtUtc);
        fd.append('ExpiresAtUtc', expiresAtUtc);
        fd.append('SendPush', String(sendPush));
        if (imageFile) fd.append('Image', imageFile);

        const res = await fetch(`${ROOT}/create`, {
            method: 'POST',
            headers: await authHeaderOnly(),   // NO content-type → let fetch set multipart boundaries
            body: fd
        }).then(guard);
        const { data } = await res.json();
        return data;                         // { id: Guid }
    },

    /* list due (worker-style helper) ------------------------*/
    due: async ({ batch = 100, offset = 0 } = {}) => {
        const res = await fetch(
            `${ROOT}/due?batch=${batch}&offset=${offset}`,
            { headers: await authHeaderOnly() }
        ).then(guard);
        const { data } = await res.json();
        return data;                         // SystemNotification[]
    },

    deactivate: async (id /* Guid */) => {
        const res = await fetch(`${ROOT}/deactivate/${id}`, {
            method: 'POST',
            headers: await authHeaderOnly()
        }).then(guard);
        return res.json();
    },

    markSent: async (id /* Guid */) => {
        const res = await fetch(`${ROOT}/mark-sent/${id}`, {
            method: 'POST',
            headers: await authHeaderOnly()
        }).then(guard);
        return res.json();
    }
};

/*──────────────────────── user-side API ──────────────────*/
const user = {
    /* feed – active notifications for a user ---------------*/
    feed: async ({ userId, userType }) => {
        const res = await fetch(`${ROOT}/feed`, {
            method: 'POST',
            headers: await jsonHeaders(),
            body: JSON.stringify({ userId, userType })
        }).then(guard);
        const { data } = await res.json();
        return data;                        // NotificationDTOs.ResponseDto[]
    },

    /* mark one notification as read ------------------------*/
    markRead: async (id /* Guid */, { userId, userType }) => {
        const res = await fetch(`${ROOT}/read/${id}`, {
            method: 'POST',
            headers: await jsonHeaders(),
            body: JSON.stringify({ userId, userType })
        }).then(guard);
        return res.json();
    }
};

/*──────────────────────── export default ────────────────*/
const SystemNotificationService = { user };
export default SystemNotificationService;
